import { React, useEffect, useState, useCallback } from 'react';
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";

import "./index.scss";

import ProfileAvatar from '../../components/ui/ProfileAvatar';
import SideModal from '../../components/ui/SideModal';
import SkeletonLoader from "../../components/ui/SkeletonLoader/index.js";

import SideNav from "../../components/PostLogin/SideNav";
import DomainTab from "../../components/PostLogin/OrganizationDetails/DomainTab";
import UsersTab from "../../components/PostLogin/OrganizationDetails/UsersTab";
import DeleteOrganizationModal from "../../components/PostLogin/OrganizationDetails/DeleteOrganizationModal";
import AddOrganizationModal from '../../components/PostLogin/Organization/AddOrganizationModel';

import { editIcon, deleteIcon, headerArrowRightIcon, businessImpactCriticalIcon, businessImpactHighIcon, businessImpactMediumIcon, businessImpactLowIcon, businessTypeIcon } from "../../utils/icons.js";
import { formatDate, formatNumber, getOrganizationImageUrl, getUserImageUrl } from "../../utils/util.js";

import { useSelector, useDispatch } from 'react-redux';
import { getOrganizationDetails, resetOrganizationDetail, resetDeleteOrganizationResponse, resetOrganizationListResponse, resetUpdateOrganizationResponse } from '../../store/actions/organizationActions.js';

const OrganizationDetails = () => {

    const organizationDetailResponse = useSelector((state) => state.organization?.organizationDetail);
    const deleteOrganizationResponse = useSelector((state)=> state.organization?.deleteOrganizationResponse)
    const updateOrganizationResponse = useSelector((state)=> state.organization?.updateOrganizationResponse);

    const dispatch = useDispatch();

    const { t } = useTranslation();

    const navigate = useNavigate();

    const { organizationId } = useParams();

    const [activeTab, setActiveTab] = useState('domain');
    const [organizationDetails, setOrganizationDetails] = useState(null);
    const [modalShow, setModalShow] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [showAddOrgSidePanel, setShowAddOrgSidePanel] = useState(false);

    const closeAddOrgSidePanel = () => {
        setShowAddOrgSidePanel(false)
    }

    const backToMainPage = () => {
        if(updateOrganizationResponse?.detail?.status === "success") {
            dispatch(resetOrganizationDetail())
            setIsLoading(true)
            fetchOrganizationsDetails()
        }
        dispatch(resetUpdateOrganizationResponse())
        closeAddOrgSidePanel()
    }

    const showModal = () => {
        setModalShow(true);
    };

    const fetchOrganizationsDetails = useCallback(()=>{
        let obj = {
            organization_id: organizationId
        }
        setIsLoading(true);
        dispatch(getOrganizationDetails(obj));
    },[dispatch, organizationId])


    const backToOrganizationPage = () => {
        dispatch(resetOrganizationDetail())
        navigate(`/organization`)
    }

    useEffect(()=>{
        fetchOrganizationsDetails()
    },[fetchOrganizationsDetails])

    useEffect(()=>{
        if(organizationDetailResponse != null){
            setOrganizationDetails(organizationDetailResponse)
            setIsLoading(false);            
            dispatch(resetOrganizationDetail())
        }
    },[organizationDetailResponse, dispatch])

    useEffect(()=>{
        if(deleteOrganizationResponse != null) {
            if(deleteOrganizationResponse?.detail?.status === "success") {
                dispatch(resetOrganizationListResponse())
                setModalShow(false)
                navigate(`/organization`)
            }
            dispatch(resetDeleteOrganizationResponse())
        }
    },[deleteOrganizationResponse, dispatch, navigate])


    return (
        <div className="organization-details-container">
            <SideNav />
            <div className="organization-page-container">
                <div className="page-header-container px-4 py-3 d-flex align-items-center justify-content-between">
                    <div className="header-left d-flex align-items-center gap-3">
                        <h1 onClick={()=>{backToOrganizationPage()}}>{t("organizationPage.heading")}</h1>
                        {headerArrowRightIcon()}
                        {!isLoading && <ProfileAvatar type="domain" name={organizationDetails?.name} imageUrl={getOrganizationImageUrl(organizationDetails)} />}
                    </div>
                    <div className="header-right d-flex gap-3">
                        <button onClick={()=>{setShowAddOrgSidePanel(true)}}>
                            <p className="d-inline-block mx-1">{editIcon()}</p>
                            {t("organizationDetailsPage.editBtn")}
                        </button>
                        <button onClick={() => showModal()}>
                            <p className="d-inline-block mx-1">{deleteIcon()}</p>
                            {t("organizationDetailsPage.deleteBtn")}
                        </button>
                    </div>
                </div>
                <div className="page-main-section p-4">
                    <div className="organization-created d-flex justify-content-between">
                        <h4>{t("organizationDetailsPage.heading")}</h4>
                        {!isLoading && <ProfileAvatar pageType={"organizationDetails"} type="profile" firstName={organizationDetails?.created_by?.name} lastName={""} imageUrl={getUserImageUrl(organizationDetails?.created_by)} />}
                    </div>
                    <div className="organization-header-container p-3 my-3 rounded">
                        {
                            isLoading ?
                            <SkeletonLoader type={"line"} count={3} currentPage={"organizationDetails"} />
                            :
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4 }}
                            >
                                <div className="d-flex justify-content-between">
                                    <div className="header-details d-flex gap-3">
                                        <p>{t("organizationDetailsPage.createdOn")} <span>{formatDate(organizationDetails?.created_at)}</span></p>
                                        {organizationDetails?.updated_at !== null && <p>{t("organizationDetailsPage.updatedOn")} <span>{formatDate(organizationDetails?.updated_at)}</span></p>}
                                    </div>
                                    <div className="organization-status d-flex gap-3">
                                        {organizationDetails?.business_impact === "critical" && <p className="crictical">{t("organizationPage.BusinessImpact")} <span className="businessimpact-type"><span className="icon">{businessImpactCriticalIcon()}</span>{organizationDetails?.business_impact}</span></p>}
                                        {organizationDetails?.business_impact === "high" && <p className="high">{t("organizationPage.BusinessImpact")} <span className="businessimpact-type"><span className="icon">{businessImpactHighIcon()}</span>{organizationDetails?.business_impact}</span></p>}
                                        {organizationDetails?.business_impact === "medium" && <p className="medium">{t("organizationPage.BusinessImpact")} <span className="businessimpact-type"><span className="icon">{businessImpactMediumIcon()}</span>{organizationDetails?.business_impact}</span></p>}
                                        {organizationDetails?.business_impact === "low" && <p className="low">{t("organizationPage.BusinessImpact")} <span className="businessimpact-type"><span className="icon">{businessImpactLowIcon()}</span>{organizationDetails?.business_impact}</span></p>}
                                        <p className="porfolio">{t("organizationPage.BusinessType")} <span className="businessimpact-type"><span className="icon">{businessTypeIcon()}</span>{organizationDetails?.type === "third_party" ? "Third party":organizationDetails?.type}</span></p>
                                    </div>
                                </div>
                                <div className="d-flex gap-3 mt-3">
                                    <div className="hacker-score-container p-3 rounded text-center">
                                        <p className="hacker-score">{formatNumber(organizationDetails?.stats?.overall_score?.obtained_score)}<span>/{organizationDetails?.stats?.overall_score?.max_score}</span></p>
                                        <p className="hacker-text">{t("organizationDetailsPage.latestHackerScore")}</p>
                                    </div>
                                    <p className="border-right mx-2"></p>
                                    <div className="total-domains-container rounded">
                                        <p className="total-domain">{organizationDetails?.stats?.domains?.total}</p>
                                        <p className="domain-text">{t("organizationDetailsPage.totalDomain")}</p>
                                    </div>
                                    <div className="total-score-container rounded">
                                        <div className="users-totol-count d-flex">
                                            <p className="total-user-count">{organizationDetails?.stats?.users?.total}</p>
                                            <div className="user-count mx-3 mt-2">
                                                <span className="active d-inline-block mx-1"></span>
                                                <span className="disbled d-inline-block"></span>
                                                <p>{t("organizationDetailsPage.totalUser")}</p>
                                            </div>
                                        </div>
                                        <div className="active-disabled-count d-flex gap-3">
                                            <p className="active-user"><span className="d-inline-block">{organizationDetails?.stats?.users?.active}</span> {t("organizationDetailsPage.activeUser")}</p>
                                            <p className="disabled-user"><span className="d-inline-block">{organizationDetails?.stats?.users?.disabled}</span> {t("organizationDetailsPage.disabledUser")}</p>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        }                        
                    </div>
                    <div className="organization-main-container p-3">
                        <div className="orgs-tab-container d-flex rounded">
                            <p className={`rounded text-center ${activeTab === "domain" ? "active" : ""}`} onClick={()=>setActiveTab("domain")}>{t("organizationDetailsPage.DomainTab")}</p>
                            <p className={`rounded text-center ${activeTab === "user" ? "active" : ""}`} onClick={()=>setActiveTab("user")}>{t("organizationDetailsPage.userTab")}</p>
                        </div>
                        {activeTab === "domain" && <DomainTab organizationDetail={organizationDetails} currentPageType={"organizationDetails"} />}
                        {activeTab === "user" && <UsersTab organizationDetail={organizationDetails} currentPageType={"organizationDetails"}/>}
                    </div>
                    {modalShow && <DeleteOrganizationModal show={modalShow} organizationDetail={organizationDetails} onHide={() => setModalShow(false)} />}
                </div>
                <SideModal showSidePanel={showAddOrgSidePanel} hideSidePanel={backToMainPage}>
                    {showAddOrgSidePanel && <AddOrganizationModal backToMainPage={backToMainPage} type={"edit"} organizationDetail={organizationDetails!== null ? organizationDetails : ""} showAddOrgSidePanel={showAddOrgSidePanel} />}
                </SideModal>
            </div>
        </div>
    )
}

export default OrganizationDetails;